import UnitStruct from "@/redux/actions/struct/implemented/UnitStruct";
import classNames from "classnames";
import { ReactNode } from "react";
import { AnyObject, Field, FormRenderProps } from "react-final-form";
import FormStruct from "../../../../../../../components/Form/FormStruct/FormStruct";
import FormValidators from "../../../../../../../components/Form/Validation/FormValidators";
import i18n from "../../../../../../../i18n";
import { AssetTypes } from "../../../../../../../model/AssetTypes";
import BFDisplaynameInput from "../../../../../../../modules/abstract-ui/forms/display-name/BFDisplayNameInput";
import BFToggle from "../../../../../../../modules/abstract-ui/forms/toggle/BFToggle";
import CacheService from "../../../../../../../services/CacheService";
import DataBusDefaults from "../../../../../../../services/DataBusDefaults";
import LanguageService from "../../../../../../../services/LanguageService";
import SubmitService from "../../../../../../../services/SubmitService";
import { RAC_TABLE_INVOICE_TYPES_COST_CENTER } from "../../../../RACAppConstants";
import {
  RACInvoiceType,
  RACInvoiceTypeCostCenter,
} from "../../../../RACInterfaces";
import "./RACInvoiceTypeCostCenterForm.scss";

interface RACInvoiceTypeCostCenterFormProps {
  invoiceType: RACInvoiceType;
  onClose: () => void;
  invoiceTypeCostCenter?: RACInvoiceTypeCostCenter;
}

const RACInvoiceTypeCostCenterForm = (
  props: RACInvoiceTypeCostCenterFormProps
) => {
  const { invoiceType, invoiceTypeCostCenter } = props;

  const initialValues = invoiceTypeCostCenter
    ? {
        status: "active",
        ...invoiceTypeCostCenter.data,
      }
    : {
        parentInvoiceType: invoiceType._id,
        displayName: {
          [LanguageService.getMandatorDefaultLanguage()]: "",
        },
        status: "active",
      };

  const submitText = props.invoiceTypeCostCenter
    ? i18n.t("Global.Buttons.save")
    : i18n.t("Global.Buttons.create");

  const formTitle = props.invoiceTypeCostCenter
    ? i18n.t(
        "rac:InvoiceType.CostCenter.Form.Edit",
        "Unterkategorie bearbeiten"
      )
    : i18n.t(
        "rac:InvoiceType.CostCenter.Form.Create",
        "Unterkategorie erstellen"
      );

  const formDescription = props.invoiceTypeCostCenter
    ? i18n.t(
        "rac:InvoiceType.CostCenter.Form.EditDescription",
        "Bearbeiten Sie die Kostenstelle für die Rechnungsart:"
      )
    : i18n.t(
        "rac:InvoiceType.CostCenter.Form.CreateDescription",
        "Erstellen Sie eine neue Kostenstelle für die Rechnungsart:"
      );

  const invoiceTypelabel = `${LanguageService.translateLabel(
    invoiceType.data.displayName
  )} (${UnitStruct.getUnit(invoiceType.data.type)?.data.label})`;
  return (
    <div className={classNames("rac-invoice-type-costCenter-form-container")}>
      <FormStruct<any>
        className="rac-invoice-type-costCenter-form"
        title={formTitle}
        submitText={submitText}
        description={`${formDescription} "${invoiceTypelabel}"`}
        initialValues={initialValues}
        render={(form: FormRenderProps<AnyObject>): ReactNode => {
          return (
            <div className="fields">
              <div className="row">
                <div className="field">
                  <Field
                    name="displayName"
                    validate={FormValidators.compose(
                      FormValidators.displayName()
                    )}
                  >
                    {({ input, meta }) => {
                      return (
                        <BFDisplaynameInput
                          className="costcenter-displayname-input"
                          label={i18n.t(
                            "rac:InvoiceTypeForm.Displayname",
                            "Name"
                          )}
                          {...input}
                          error={meta.touched ? meta.error : undefined}
                        />
                      );
                    }}
                  </Field>
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <BFToggle
                    label={i18n.t(
                      "rac:InvoiceType.CostCenter.Form.Status",
                      "Aktiv"
                    )}
                    checked={form.values.status === "active"}
                    onChange={(checked: boolean) => {
                      form.form.mutators.setValue(
                        "status",
                        checked ? "active" : "inactive"
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          );
        }}
        onSubmit={async (values) => {
          const result = (await SubmitService.submitDataAsync({
            type: "asset",
            assetType: AssetTypes.InvoiceConfig.CostCenter,
            data: {
              _id: props.invoiceTypeCostCenter?._id,
              data: {
                displayName: values.displayName,
                status: values.status,
                parentInvoiceType: values.parentInvoiceType,
              },
            } as Partial<RACInvoiceTypeCostCenter>,
            ignorePropChecks: true,
            ignoreSubmitValidation: true,
          })) as RACInvoiceTypeCostCenter;
          CacheService.updateDataInCaches(result._id, result);
          props.onClose();

          if (!props.invoiceTypeCostCenter) {
            DataBusDefaults.reload({
              identifiers: [RAC_TABLE_INVOICE_TYPES_COST_CENTER],
            });
          }
        }}
        onAbort={(formProps) => {
          formProps.form.reset();
          props.onClose();
        }}
      />
    </div>
  );
};

export default RACInvoiceTypeCostCenterForm;
